/**
 * Utility functions for determining collection types and restricting item types
 * based on existing collection content.
 */

/**
 * Determines the collection type based on the category and existing items
 * @param category - The collection category
 * @param items - Array of items in the collection
 * @returns The determined collection type
 */
export const determineCollectionType = (
  category: string,
  items: Array<{ name: string; svg?: string; count?: number }>
): string => {
  // If no items, return custom as default
  if (items.length === 0) {
    return "custom";
  }

  // If items have SVG data, it's likely a Number Sense collection
  if (items.some(item => item.svg)) {
    return "numberSense";
  }

  // Check for specific patterns in item names
  const itemNames = items.map(item => item.name);
  
  // Check for periodic table elements (format: "Symbol - Name - Number")
  if (itemNames.some(name => name.includes(" - ") && name.split(" - ").length === 3)) {
    return "periodicTable";
  }

  // Check for nursing terms based on category
  if (category === "Nursing") {
    return "nursingTerms";
  }

  // Check for science terms based on category
  if (category === "Science") {
    return "scienceTerms";
  }

  // Check for math problems (containing operators)
  if (itemNames.some(name => /[+\-×÷]/.test(name))) {
    return "mathProblems";
  }

  // Default to custom for other cases
  return "custom";
};

/**
 * Gets the allowed item types for a given collection type
 * @param collectionType - The type of the collection
 * @returns Array of allowed item types
 */
export const getAllowedItemTypes = (collectionType: string): string[] => {
  switch (collectionType) {
    case "numberSense":
      return ["numberSense"];
    case "periodicTable":
      return ["periodicTable"];
    case "nursingTerms":
      return ["nursingTerms"];
    case "scienceTerms":
      return ["scienceTerms"];
    case "mathProblems":
      return ["mathProblems"];
    case "custom":
    default:
      return ["custom"];
  }
};

/**
 * Gets the display name for an item type
 * @param itemType - The item type
 * @returns The display name
 */
export const getItemTypeDisplayName = (itemType: string): string => {
  switch (itemType) {
    case "numberSense":
      return "Number Sense";
    case "periodicTable":
      return "Periodic Table";
    case "nursingTerms":
      return "Nursing Terms";
    case "scienceTerms":
      return "Science Terms";
    case "mathProblems":
      return "Math Problems";
    case "custom":
      return "Custom Item";
    default:
      return "Custom Item";
  }
};
